/**
 * TemplateCommandPopup — Slash command popup for legal document templates
 * Shows when user types "/" in chat input
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Sparkles, ChevronLeft, FileText } from 'lucide-react';
import { LegalTemplate, getTemplatesByCategory, searchTemplates } from '../../data/legalTemplates';

interface TemplateCommandPopupProps {
  /** Current text in the input (after the /) */
  filterText: string;
  /** Called when user generates a prompt from a template */
  onGenerate: (prompt: string) => void;
  /** Called to close the popup */
  onClose: () => void;
}

const TemplateCommandPopup: React.FC<TemplateCommandPopupProps> = ({ filterText, onGenerate, onClose }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<LegalTemplate | null>(null);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const popupRef = useRef<HTMLDivElement>(null);

  // Filter templates based on search query
  const query = filterText.trim();
  const filteredTemplates = query ? searchTemplates(query) : [];
  const groupedTemplates = query ? null : getTemplatesByCategory();

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedTemplate) setSelectedTemplate(null);
        else onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [selectedTemplate, onClose]);

  // Handle form submission
  const handleGenerate = () => {
    if (!selectedTemplate) return;
    // Check required fields
    const missing = selectedTemplate.fields.filter(f => f.required && !formData[f.id]?.trim());
    if (missing.length > 0) {
      alert(`Please fill required fields: ${missing.map(f => f.label).join(', ')}`);
      return;
    }
    const prompt = selectedTemplate.buildPrompt(formData);
    onGenerate(prompt);
    setSelectedTemplate(null);
    setFormData({});
  };

  // Template form view
  if (selectedTemplate) {
    return (
      <div ref={popupRef} className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-2xl shadow-2xl border border-gray-200 max-h-[70vh] overflow-y-auto z-50">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 rounded-t-2xl">
          <button onClick={() => { setSelectedTemplate(null); setFormData({}); }} className="p-1 hover:bg-gray-100 rounded-lg">
            <ChevronLeft size={16} className="text-gray-500" />
          </button>
          <span className="text-lg">{selectedTemplate.icon}</span>
          <div className="flex-1">
            <div className="text-sm font-semibold text-gray-800">{selectedTemplate.name}</div>
            <div className="text-[10px] text-gray-500">{selectedTemplate.description}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-3">
          {selectedTemplate.fields.map(field => (
            <div key={field.id}>
              <label className="text-[11px] font-semibold text-gray-600 block mb-1">
                {field.label} {field.required && <span className="text-red-400">*</span>}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  value={formData[field.id] || ''}
                  onChange={e => setFormData(p => ({ ...p, [field.id]: e.target.value }))}
                  placeholder={field.placeholder}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/20 resize-none"
                />
              ) : field.type === 'select' ? (
                <select
                  value={formData[field.id] || ''}
                  onChange={e => setFormData(p => ({ ...p, [field.id]: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 bg-white"
                >
                  <option value="">Select...</option>
                  {field.options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <input
                  type="text"
                  value={formData[field.id] || ''}
                  onChange={e => setFormData(p => ({ ...p, [field.id]: e.target.value }))}
                  placeholder={field.placeholder}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400/20"
                />
              )}
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-4 py-3 flex gap-2 rounded-b-2xl">
          <button onClick={() => { setSelectedTemplate(null); setFormData({}); }}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-xs text-gray-600 hover:bg-gray-50 font-medium">
            Cancel
          </button>
          <button onClick={handleGenerate}
            className="flex-1 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl text-xs font-semibold hover:shadow-lg flex items-center justify-center gap-1.5">
            <Sparkles size={13} /> Generate Draft
          </button>
        </div>
      </div>
    );
  }

  // Template list view
  return (
    <div ref={popupRef} className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-2xl shadow-2xl border border-gray-200 max-h-[60vh] overflow-y-auto z-50">
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-2 rounded-t-2xl">
        <FileText size={16} className="text-indigo-500" />
        <span className="text-sm font-semibold text-gray-700 flex-1">Legal Templates</span>
        <span className="text-[10px] text-gray-400">Type to search</span>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
          <X size={14} className="text-gray-400" />
        </button>
      </div>

      {/* Search results */}
      {query && (
        <div className="p-2">
          {filteredTemplates.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-400">No templates matching "{query}"</div>
          ) : (
            filteredTemplates.map(t => (
              <button key={t.id} onClick={() => setSelectedTemplate(t)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-indigo-50 text-left transition-colors">
                <span className="text-lg">{t.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-gray-800 truncate">{t.name}</div>
                  <div className="text-[10px] text-gray-500 truncate">{t.description}</div>
                </div>
                <span className="text-[9px] text-indigo-400 bg-indigo-50 px-2 py-0.5 rounded-full font-medium">{t.category}</span>
              </button>
            ))
          )}
        </div>
      )}

      {/* Grouped list (no search) */}
      {!query && groupedTemplates && (
        <div className="p-2">
          {Object.entries(groupedTemplates).map(([category, templates]) => (
            <div key={category} className="mb-3">
              <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider px-3 py-1">{category}</div>
              {templates.map(t => (
                <button key={t.id} onClick={() => setSelectedTemplate(t)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-indigo-50 text-left transition-colors">
                  <span className="text-base">{t.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800 truncate">{t.name}</div>
                    <div className="text-[10px] text-gray-400 truncate">{t.description}</div>
                  </div>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Hint */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
        <div className="text-[10px] text-gray-400 text-center">
          Type <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600 font-mono">/bail</kbd> or <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600 font-mono">/notice</kbd> to filter • <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600 font-mono">Esc</kbd> to close
        </div>
      </div>
    </div>
  );
};

export default TemplateCommandPopup;
